import {ENV_CONFIGS} from '../constants/envs';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import omitBy from 'lodash/omitBy';
import {DM_URN_TYPE, OSS_URN_TYPE} from '../constants/attachments';
import {withoutIssueTypeId} from '../utils/issues-helpers';
import axios from 'axios';
import {
  ISSUES_ACTIVITIES_ENDPOINT,
  ISSUES_ATTACHMENTS_ENDPOINT,
  ISSUES_COMMENTS_ENDPOINT,
  ISSUES_CURRENT_USER_PERMISSIONS_ENDPOINT,
  ISSUES_ENDPOINT,
  ISSUES_LINKS_ENDPOINT,
  ISSUES_PERMISSIONS_BULK_OPERATION_ENDPOINT,
  ISSUES_REQUIRED_FIELDS_ENDPOINT,
  ISSUE_ATTRIBUTE_DEFINITIONS_ENDPOINT,
  ISSUE_ATTRIBUTE_MAPPINGS_ENDPOINT,
  ISSUE_OSS_OBJECTS_ENDPOINT,
  ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT,
  ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT_ROOT_CAUSES,
  ISSUE_ROOT_CAUSE_ENDPOINT,
  ISSUE_SUB_TYPES_ENDPOINT,
  ISSUE_TEMPLATES_ENDPOINT,
  ISSUE_TEMPLATE_CATEGORIES_ENDPOINT,
  ISSUE_TYPES_ENDPOINT,
  ISSUE_TYPES_ENDPOINT_SUB_TYPES,
  ISSUES_PERMISSIONS_ENDPOINT
} from '../constants/issues-v2-endpoints';
import {
  buildActivityGetParams,
  buildAttachmentGetParams,
  buildIssuesBatchGetRequest,
  buildExportBCFGetParams,
  buildGetParams,
  buildTemplateCategoriesGetParams,
  buildTemplatesGetParams
} from '../utils/issues-v2-params-helper';
import {mapEntityItems, handleIssuesResponse} from '../utils/issues-v2-api-helper';
import {addAuthToken} from '../utils/addAuthToken';
import {ROOT_CAUSE_CATEGORY_READ_LIMIT} from '../constants/issue-root-causes';
import {ISSUE_TYPES_READ_LIMIT} from '../constants/issue-types';
import {mapClientToServerRequiredFields, mapServerToClientRequiredFields} from '../utils/required-fields-helper';
import {tokenExpired} from '../app/actions';
import lodashGet from 'lodash/get';

export default class {
  constructor(dispatch, getState, shouldConnectCOSV3) {
    const defaultHeaders = {'Content-Type': 'application/json'};
    const cosv3Headers = {'x-ads-dest': 'cosv3'};
    this.dispatch = dispatch;
    this.getState = getState;
    this.axiosClient = axios.create({
      headers: shouldConnectCOSV3 ? {...defaultHeaders, ...cosv3Headers} : defaultHeaders
    });
  }

  setAxiosClient(apiConfig) {
    const {containerId} = apiConfig;
    const issuesV2ApiUrl = `${ENV_CONFIGS.APIGEE_HOST}${ENV_CONFIGS.ISSUES_V2}`;
    this.axiosClient.defaults.baseURL = `${issuesV2ApiUrl}/containers/${containerId}/`;
    this.setInceptors();
  }

  setInceptors() {
    this.axiosClient.interceptors.request.use(config => addAuthToken(this.getState, this.dispatch, config));
    this.axiosClient.interceptors.response.use(
      response => response.data,
      error => {
        if (lodashGet(error, 'response.status') === 401) {
          this.dispatch(tokenExpired());
        }
        return Promise.reject(error);
      }
    );
  }

  setConfig(apiConfig) {
    this.setAxiosClient(apiConfig);
  }

  getIssues(query = {}) {
    return this.axiosClient.get(ISSUES_ENDPOINT, {params: buildGetParams(query)}).then(handleIssuesResponse);
  }
  getIssuesBatchGet(query = {}) {
    return this.axiosClient
      .post(`${ISSUES_ENDPOINT}:batchGet`, buildIssuesBatchGetRequest(query))
      .then(handleIssuesResponse);
  }
  getIssue(issueId) {
    return this.axiosClient.get(`${ISSUES_ENDPOINT}/${issueId}`);
  }

  createIssue(attributes) {
    return this.axiosClient.post(ISSUES_ENDPOINT, withoutIssueTypeId(attributes));
  }

  updateIssue(issueId, attributes) {
    return this.axiosClient.patch(`${ISSUES_ENDPOINT}/${issueId}`, withoutIssueTypeId(attributes));
  }

  getIssuesPermissions() {
    return this.axiosClient.get(ISSUES_PERMISSIONS_ENDPOINT).then(({results}) => results);
  }

  updateIssuesPermissions(permissionId, permissionData) {
    return this.axiosClient.patch(`${ISSUES_PERMISSIONS_ENDPOINT}/${permissionId}`, permissionData);
  }

  createBulkIssuesPermissions(permissions) {
    return this.axiosClient.post(ISSUES_PERMISSIONS_BULK_OPERATION_ENDPOINT, permissions);
  }

  deleteIssuesPermissions(permissionId) {
    return this.axiosClient.delete(`${ISSUES_PERMISSIONS_ENDPOINT}/${permissionId}`);
  }

  getCurrentUserPermissions() {
    return this.axiosClient.get(ISSUES_CURRENT_USER_PERMISSIONS_ENDPOINT);
  }

  getIssueTemplateById(templateId) {
    return this.axiosClient.get(`${ISSUE_TEMPLATES_ENDPOINT}/${templateId}`);
  }

  getIssueTemplates(queryParams) {
    return this.axiosClient.get(ISSUE_TEMPLATES_ENDPOINT, {params: buildTemplatesGetParams(queryParams)});
  }

  createIssueTemplate(attributes) {
    return this.axiosClient.post(ISSUE_TEMPLATES_ENDPOINT, omitBy(attributes, isNullOrUndefined));
  }

  updateIssueTemplate(id, attributes) {
    return this.axiosClient.patch(`${ISSUE_TEMPLATES_ENDPOINT}/${id}`, attributes);
  }

  deleteIssueTemplate(id) {
    return this.axiosClient.delete(`${ISSUE_TEMPLATES_ENDPOINT}/${id}`);
  }

  getIssueTemplateCategories(queryParams) {
    return this.axiosClient.get(ISSUE_TEMPLATE_CATEGORIES_ENDPOINT, {
      params: buildTemplateCategoriesGetParams(queryParams)
    });
  }

  createIssueTemplateCategory(attributes) {
    return this.axiosClient.post(ISSUE_TEMPLATE_CATEGORIES_ENDPOINT, {...attributes});
  }

  updateIssueTemplateCategory(id, attributes) {
    return this.axiosClient.patch(`${ISSUE_TEMPLATE_CATEGORIES_ENDPOINT}/${id}`, attributes);
  }

  deleteIssueTemplateCategory(id, templatesTarget) {
    return this.axiosClient.delete(`${ISSUE_TEMPLATE_CATEGORIES_ENDPOINT}/${id}`, {
      params: {
        templatesTarget
      }
    });
  }

  createOSSObject() {
    return this.axiosClient.post(ISSUE_OSS_OBJECTS_ENDPOINT);
  }

  getAttachments(issueId, {offset}) {
    return this.axiosClient.get(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_ATTACHMENTS_ENDPOINT}`, {
      params: buildAttachmentGetParams(offset)
    });
  }

  createAttachmentUrn(issueId, name) {
    return this.axiosClient.post(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_ATTACHMENTS_ENDPOINT}`, {
      urnType: OSS_URN_TYPE,
      name
    });
  }

  finalizeAttachmentUpload(issueId, attachmentId) {
    return this.axiosClient.post(
      `${ISSUES_ENDPOINT}/${issueId}/${ISSUES_ATTACHMENTS_ENDPOINT}/${attachmentId}`,
      {},
      {
        params: {
          task: 'post-upload-process'
        }
      }
    );
  }

  createAttachmentFromDocs(issueId, {name, urn}) {
    return this.axiosClient.post(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_ATTACHMENTS_ENDPOINT}`, {
      urnType: DM_URN_TYPE,
      name,
      urn
    });
  }

  deleteAttachment(issueId, attachmentId) {
    return this.axiosClient.delete(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_ATTACHMENTS_ENDPOINT}/${attachmentId}`);
  }

  createComment(issueId, body) {
    return this.axiosClient.post(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_COMMENTS_ENDPOINT}`, {issueId, body});
  }

  linkIssueToEntity(issueId, addedLinks) {
    return this.axiosClient.post(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_LINKS_ENDPOINT}`, mapEntityItems(addedLinks));
  }

  unlinkIssueFromEntity(issueId, removedLink) {
    return this.axiosClient.post(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_LINKS_ENDPOINT}:delete`, {
      items: [{linkedItemId: removedLink.id, domain: removedLink.domain, linkedItemType: removedLink.type}]
    });
  }

  getActivities({issueId, offset}) {
    return this.axiosClient.get(`${ISSUES_ENDPOINT}/${issueId}/${ISSUES_ACTIVITIES_ENDPOINT}`, {
      params: buildActivityGetParams(offset)
    });
  }

  exportBcf(modelUnit, modelType, projectId, userId, fileName, query) {
    const data = {
      modelUnit,
      modelType,
      recipients: [{userId}],
      projectId,
      fileName
    };
    return this.axiosClient.post(`${ISSUES_ENDPOINT}/bcf/export`, data, {params: buildExportBCFGetParams(query)});
  }

  // Issues-attributes apis

  getIssueAttributeDefinitions(offset) {
    return this.axiosClient.get(ISSUE_ATTRIBUTE_DEFINITIONS_ENDPOINT, {params: {limit: 200, offset}});
  }

  getIssueAttributeMappings(offset) {
    return this.axiosClient.get(ISSUE_ATTRIBUTE_MAPPINGS_ENDPOINT, {params: {limit: 200, offset}});
  }

  createIssueAttributeDefinition(attributes) {
    return this.axiosClient.post(ISSUE_ATTRIBUTE_DEFINITIONS_ENDPOINT, attributes);
  }

  updateIssueAttributeDefinition(id, attributes) {
    return this.axiosClient.patch(`${ISSUE_ATTRIBUTE_DEFINITIONS_ENDPOINT}/${id}`, attributes);
  }

  deleteIssueAttributeDefinition(id) {
    return this.axiosClient.delete(`${ISSUE_ATTRIBUTE_DEFINITIONS_ENDPOINT}/${id}`);
  }

  updateIssueTypeMappings(attributes) {
    return this.axiosClient.put(ISSUE_ATTRIBUTE_MAPPINGS_ENDPOINT, attributes);
  }

  // Issues-root-causes apis

  createIssueRootCause(attributes) {
    return this.axiosClient.post(
      `${ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT}/${attributes.parentId}/${ISSUE_ROOT_CAUSE_ENDPOINT}`,
      {title: attributes.title, isActive: attributes.isActive}
    );
  }

  getIssueRootCauses() {
    return this.axiosClient
      .get(ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT, {
        params: {
          include: ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT_ROOT_CAUSES,
          limit: ROOT_CAUSE_CATEGORY_READ_LIMIT
        }
      })
      .then(response => response.results);
  }

  createIssueRootCauseCategory(attributes) {
    return this.axiosClient.post(ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT, attributes);
  }

  updateIssueRootCause(id, parentId = null, attributes) {
    if (parentId) {
      return this.axiosClient.patch(`${ISSUE_ROOT_CAUSE_ENDPOINT}/${id}`, attributes);
    }
    return this.axiosClient.patch(`${ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT}/${id}`, attributes, {
      params: {include: ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT_ROOT_CAUSES}
    });
  }

  deleteIssueRootCauseCategory(id) {
    return this.axiosClient.delete(`${ISSUE_ROOT_CAUSE_CATEGORIES_ENDPOINT}/${id}`);
  }

  deleteIssueRootCause(id) {
    return this.axiosClient.delete(`${ISSUE_ROOT_CAUSE_ENDPOINT}/${id}`);
  }

  // Issues-type apis

  createIssueSubType(attributes) {
    return this.axiosClient.post(`${ISSUE_TYPES_ENDPOINT}/${attributes.parentId}/${ISSUE_SUB_TYPES_ENDPOINT}`, {
      title: attributes.title,
      isActive: attributes.isActive
    });
  }

  getIssueTypes() {
    return this.axiosClient
      .get(ISSUE_TYPES_ENDPOINT, {
        params: {
          include: ISSUE_TYPES_ENDPOINT_SUB_TYPES,
          limit: ISSUE_TYPES_READ_LIMIT
        }
      })
      .then(response => response.results);
  }

  createIssueType(attributes) {
    return this.axiosClient.post(ISSUE_TYPES_ENDPOINT, attributes, {
      params: {include: ISSUE_TYPES_ENDPOINT_SUB_TYPES}
    });
  }

  updateIssueType(id, parentId = null, attributes) {
    if (parentId) {
      return this.axiosClient.patch(`${ISSUE_SUB_TYPES_ENDPOINT}/${id}`, attributes);
    }
    return this.axiosClient.patch(`${ISSUE_TYPES_ENDPOINT}/${id}`, attributes, {
      params: {include: ISSUE_TYPES_ENDPOINT_SUB_TYPES}
    });
  }

  // issues required-fields apis

  getRequiredFields() {
    return this.axiosClient
      .get(ISSUES_REQUIRED_FIELDS_ENDPOINT)
      .then(response => mapServerToClientRequiredFields(response.results));
  }
  updateRequiredFields(rawRequiredFields) {
    const requiredFields = mapClientToServerRequiredFields(rawRequiredFields);
    return this.axiosClient
      .post(ISSUES_REQUIRED_FIELDS_ENDPOINT, {
        fields: requiredFields
      })
      .then(response => mapServerToClientRequiredFields(response.results));
  }
}
